import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ARApiError } from '@relayter/core';
import { NucDialogConfigModel } from '@relayter/rubber-duck';
import { defer, filter, finalize, Observable, retry, shareReplay, Subject, tap, throwError, timer } from 'rxjs';
import { ErrorConstants } from '../api/error.constants';
import { OAuthApiService, TokenRefreshError } from '../api/services/oauth.api.service';
import { UserService } from './user.service';

/** Owns access-token refreshes and publishes when an authenticated session is restored. */
@Injectable({ providedIn: 'root' })
export class SessionService {
    /**
     * Temporary failures for which retrying a token refresh can succeed:
     * 0 = browser/network error, 408 = request timeout, 502 = bad gateway,
     * 503 = service unavailable, 504 = gateway timeout.
     */
    private static readonly RETRYABLE_REFRESH_HTTP_STATUSES: readonly number[] = [0, 408, 502, 503, 504];
    private static readonly MAX_NETWORK_REFRESH_RETRIES = 5;
    private static readonly INITIAL_RETRY_DELAY = 1_000;
    private static readonly MAX_RETRY_DELAY = 8_000;

    private readonly destroyRef = inject(DestroyRef);
    private readonly oauthApiService = inject(OAuthApiService);
    private readonly userService = inject(UserService);
    private readonly sessionRestoredSubject = new Subject<string>();
    private isTerminatingSession = false;
    private refreshRequest$: Observable<string> | null = null;

    /** Emits after every successful access-token refresh. */
    public readonly sessionRestored$ = this.sessionRestoredSubject.asObservable();

    constructor() {
        this.userService
            .getUserUpdates()
            .pipe(
                filter((user) => user !== null),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.isTerminatingSession = false;
            });
    }

    /** Refreshes the access token. Concurrent callers share the same request. */
    public refreshAccessToken(): Observable<string> {
        if (!this.refreshRequest$) {
            const request$ = this.oauthApiService.tokenRefresh().pipe(
                tap((accessToken) => this.sessionRestoredSubject.next(accessToken)),
                finalize(() => {
                    if (this.refreshRequest$ === request$) {
                        this.refreshRequest$ = null;
                    }
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            );
            this.refreshRequest$ = request$;
        }

        return this.refreshRequest$;
    }

    /**
     * Restores a session after a transport disconnect. Only unavailable-network
     * failures retry; an invalid or revoked refresh token fails immediately.
     */
    public restoreSession(): Observable<string> {
        return defer(() => this.refreshAccessToken()).pipe(
            retry({
                count: SessionService.MAX_NETWORK_REFRESH_RETRIES,
                delay: (error: unknown, retryCount) => (this.isRetryableRefreshError(error) ? timer(this.getRetryDelay(retryCount)) : throwError(() => error))
            })
        );
    }

    /** Starts the appropriate logout flow once, even when several requests observe the same refresh failure. */
    public handleRefreshFailure(error: unknown, returnUrl?: string): void {
        if (this.isTerminatingSession) {
            return;
        }

        this.isTerminatingSession = true;
        const errorCode = this.getErrorCode(error);

        if (errorCode === ErrorConstants.API_ERROR_CODES.SESSION_REVOKED) {
            const dialogConfig = new NucDialogConfigModel('Session expired', 'There is another session active with this account. You will now be logged out.');
            this.userService.forcedSessionClose(dialogConfig, returnUrl);
            return;
        }

        if (errorCode === ErrorConstants.API_ERROR_CODES.SESSION_EXPIRED) {
            const dialogConfig = new NucDialogConfigModel('Session expired', 'The session is expired. You will now be logged out.');
            this.userService.forcedSessionClose(dialogConfig, returnUrl);
            return;
        }

        this.userService.clearUserAndRedirectToLogin(returnUrl);
    }

    private getErrorCode(error: unknown): number | undefined {
        if (typeof error !== 'object' || error === null) return undefined;

        const errorResponse = error as Record<string, unknown>;
        const nestedError = typeof errorResponse['error'] === 'object' && errorResponse['error'] !== null ? errorResponse['error'] : {};
        const errorCode = errorResponse['code'] ?? errorResponse['error_code'] ?? nestedError['code'] ?? nestedError['error_code'];

        return typeof errorCode === 'number' ? errorCode : undefined;
    }

    private isRetryableRefreshError(error: unknown): error is TokenRefreshError {
        return (
            error instanceof ARApiError &&
            typeof (error as Partial<TokenRefreshError>).httpStatus === 'number' &&
            SessionService.RETRYABLE_REFRESH_HTTP_STATUSES.includes((error as TokenRefreshError).httpStatus)
        );
    }

    private getRetryDelay(retryCount: number): number {
        return Math.min(SessionService.INITIAL_RETRY_DELAY * 2 ** (retryCount - 1), SessionService.MAX_RETRY_DELAY);
    }
}
